import { FC, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { isNullOrEmpty } from '@liveconnect/communities-ui'
import { Icon } from '@liveconnect/icons'
import classNames from 'classnames'

import { Reactions, ReactionTypeEnum } from '../../../core/posts/types'
import usePosts from '../../../core/posts/usePosts'
import useActivities from '../../../core/activities/useActivities'
import useGTM from '../../../utils/useGTM'

import './styles.scss'


interface PostData {
  content_title: string
  content_channel: string
  content_topic: string
  content_highlight: number
  content_author: string
  content_date: string
}
export interface ReactionComponentProps {
  fingerUpCount?: number
  likeCount?: number
  myReaction: ReactionTypeEnum
  isPost: boolean
  takenId: string
  commentId?: string
  disabled?: boolean
  inActivity?: boolean
  postData?: PostData | undefined
}

export const ReactionComponent: FC<ReactionComponentProps> = ({
  takenId,
  commentId,
  isPost,
  fingerUpCount,
  likeCount,
  myReaction,
  disabled,
  inActivity,
  postData,
}): JSX.Element => {
  const { t } = useTranslation()
  const { updatePostReaction, updateCommentReaction } = usePosts()
  const { updateCommentActivityReaction } = useActivities()
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [internalReaction, setInternalReaction] = useState<ReactionTypeEnum>(
    myReaction ?? ReactionTypeEnum.NONE
  )
  const [internalFingerUpCount, setInternalFingerUpCount] = useState<number>(
    fingerUpCount ?? 0
  )
  const [internalLikeCount, setInternalLikeCount] = useState<number>(
    likeCount ?? 0
  )
  const { pushDataLayer } = useGTM()

  const getInternalCount = useCallback(
    (reactionType: ReactionTypeEnum): number => {
      switch (reactionType) {
        case ReactionTypeEnum.NONE:
          return 0
        case ReactionTypeEnum.FINGERUP:
          if (
            internalReaction === ReactionTypeEnum.FINGERUP &&
            internalFingerUpCount === 0
          )
            return 1
          return internalFingerUpCount
        case ReactionTypeEnum.LIKE:
          if (
            internalReaction === ReactionTypeEnum.LIKE &&
            internalLikeCount === 0
          )
            return 1
          return internalLikeCount
      }
    },
    [internalReaction, internalFingerUpCount, internalLikeCount]
  )

  const handleReaction = async (
    reactionType: ReactionTypeEnum,
    reactionTypeRemove: ReactionTypeEnum
  ) => {
    let reactionResult: Reactions | undefined
    setIsSubmitting(true)
    try {
      if (isPost) {
        reactionResult = await updatePostReaction(takenId, reactionType)
        pushDataLayer({
          event:
            reactionType !== ReactionTypeEnum.NONE
              ? 'review_icon_add_community'
              : 'review_icon_delete_community',
          category: 'interaction',
          action:
            reactionType !== ReactionTypeEnum.NONE
              ? 'review_icon_add'
              : 'review_icon_delete',
          extraParams: {
            ...postData,
            review_icon:
              reactionType !== ReactionTypeEnum.NONE
                ? reactionType
                : reactionTypeRemove,
          },
        })
      } else if (commentId && !isNullOrEmpty(commentId)) {
        if (inActivity) {
          reactionResult = await updateCommentActivityReaction(
            takenId,
            commentId,
            reactionType
          )
        } else {
          reactionResult = await updateCommentReaction(
            takenId,
            commentId,
            reactionType
          )
        }
      }

      if (reactionResult) {
        setInternalReaction(reactionResult.myReaction)
        setInternalFingerUpCount(reactionResult.fingerUpCount)
        setInternalLikeCount(reactionResult.likeCount)
      } else {
        setInternalReaction(ReactionTypeEnum.NONE)
        setInternalFingerUpCount(0)
        setInternalLikeCount(0)
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="Reactions">
      <div
        className={classNames('Reactions__thumbsUp', {
          current: internalReaction === ReactionTypeEnum.FINGERUP,
        })}
      >
        <button
          onClick={() =>
            handleReaction(
              internalReaction !== ReactionTypeEnum.FINGERUP
                ? ReactionTypeEnum.FINGERUP
                : ReactionTypeEnum.NONE,
              ReactionTypeEnum.FINGERUP
            )
          }
          title={t('post.reactions.finger_up')}
          disabled={isSubmitting || disabled === true}
        >
          <Icon name="like" />
        </button>
        {getInternalCount(ReactionTypeEnum.FINGERUP) !== 0 && (
          <span className="Reactions__thumbsUp__count">
            {getInternalCount(ReactionTypeEnum.FINGERUP)}
          </span>
        )}
      </div>
      <div
        className={classNames('Reactions__likeUp', {
          current: internalReaction === ReactionTypeEnum.LIKE,
        })}
      >
        <button
          onClick={() =>
            handleReaction(
              internalReaction !== ReactionTypeEnum.LIKE
                ? ReactionTypeEnum.LIKE
                : ReactionTypeEnum.NONE,
              ReactionTypeEnum.LIKE
            )
          }
          title={t('post.reactions.like_up')}
          disabled={isSubmitting || disabled === true}
        >
          <Icon name="enthusiasm" />
        </button>
        {getInternalCount(ReactionTypeEnum.LIKE) !== 0 && (
          <span className="Reactions__likeUp__count">
            {getInternalCount(ReactionTypeEnum.LIKE)}
          </span>
        )}
      </div>
    </div>
  )
}
